/**
 * API Client for Django Backend with JWT Authentication
 */

// Если задана переменная окружения - берем её как есть. Иначе используем /api для прокси.
const API_BASE = process.env.REACT_APP_API_BASE || '/api';

/**
 * Get stored access token
 */
function getAccessToken() {
  const session = localStorage.getItem('auth_session')
  if (session) {
    try {
      const data = JSON.parse(session)
      return data.access_token || null
    } catch (e) {
      return null
    }
  }
  return null
}

/**
 * Base fetch function with error handling and JWT authentication
 */
async function apiRequest(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`
  const token = getAccessToken()

  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  }

  // Add JWT token if available
  if (token && !options.skipAuth) {
    config.headers['Authorization'] = `Bearer ${token}`
  }

  // Remove Content-Type for FormData (браузер сам установит правильный Content-Type с boundary)
  if (config.body instanceof FormData) {
    delete config.headers['Content-Type']
  }

  try {
    const response = await fetch(url, config)

    // Handle 401 Unauthorized - token expired
    if (response.status === 401 && token && !options.skipAuth) {
      // Try to refresh token
      const refreshed = await refreshAccessToken()
      if (refreshed) {
        // Retry request with new token
        config.headers['Authorization'] = `Bearer ${getAccessToken()}`
        const retryResponse = await fetch(url, config)
        return handleResponse(retryResponse)
      } else {
        // Refresh failed, clear session
        localStorage.removeItem('auth_session')
        window.location.href = '/login'
        throw new Error('Session expired. Please login again.')
      }
    }

    return handleResponse(response)
  } catch (error) {
    console.error('API Request Error:', error)
    return { data: null, error: error.message || 'Network error' }
  }
}

/**
 * Handle API response
 */
async function handleResponse(response) {
  // Handle non-JSON responses
  const contentType = response.headers.get('content-type')
  if (!contentType || !contentType.includes('application/json')) {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    return { data: null, error: null }
  }

  const data = await response.json()

  if (!response.ok) {
    const error = data.error || data.detail || data.message || `HTTP error! status: ${response.status}`
    throw new Error(error)
  }

  return { data, error: null }
}

/**
 * Refresh access token
 */
async function refreshAccessToken() {
  const session = localStorage.getItem('auth_session')
  if (!session) return false

  try {
    const sessionData = JSON.parse(session)
    const refreshToken = sessionData.refresh_token

    if (!refreshToken) return false

    const response = await fetch(`${API_BASE}/auth/refresh/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })

    if (!response.ok) return false

    const data = await response.json()

    // Update session with new access token
    sessionData.access_token = data.access_token
    localStorage.setItem('auth_session', JSON.stringify(sessionData))

    return true
  } catch (error) {
    console.error('Token refresh error:', error)
    return false
  }
}

/**
 * GET request
 */
export async function apiGet(endpoint, params = {}) {
  const queryString = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      queryString.append(key, value)
    }
  })

  const url = queryString.toString() ? `${endpoint}?${queryString}` : endpoint
  return apiRequest(url, { method: 'GET' })
}

/**
 * POST request
 */
export async function apiPost(endpoint, body = {}, options = {}) {
  // Если body - FormData, не используем JSON.stringify
  const requestBody = body instanceof FormData ? body : JSON.stringify(body);
  return apiRequest(endpoint, {
    method: 'POST',
    body: requestBody,
    ...options,
  })
}

/**
 * PATCH request
 */
export async function apiPatch(endpoint, body = {}) {
  // Если body - FormData, не используем JSON.stringify
  const requestBody = body instanceof FormData ? body : JSON.stringify(body);
  return apiRequest(endpoint, {
    method: 'PATCH',
    body: requestBody,
  })
}

/**
 * PUT request
 */
export async function apiPut(endpoint, body = {}) {
  return apiRequest(endpoint, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

/**
 * DELETE request
 */
export async function apiDelete(endpoint) {
  return apiRequest(endpoint, { method: 'DELETE' })
}

export default {
  get: apiGet,
  post: apiPost,
  patch: apiPatch,
  put: apiPut,
  delete: apiDelete,
}
